// Punto unico da cui ogni chiamata /api passa: aggiunge il token di sessione
// (se presente) e intercetta le risposte 401 per forzare il logout. Il token
// si legge SEMPRE direttamente da localStorage (non da una closure impostata
// via useEffect in AuthContext): appena dopo il login, il primo fetch parte
// nello stesso istante in cui React monta la Shell, che può capitare prima
// che l'effect di AuthContext abbia avuto il tempo di girare — leggendo da
// localStorage (già scritto in modo sincrono da AuthContext.entra()) si evita
// quella corsa. Il callback di "sessione scaduta" invece può arrivare con un
// tick di ritardo senza problemi: si attiva solo su richieste successive.
const CHIAVE_TOKEN = "dbi_token";
let onNonAutorizzato = () => {};

export function configuraAuth({ onNonAutorizzato: o }) {
  onNonAutorizzato = o;
}

function tokenCorrente() {
  try { return localStorage.getItem(CHIAVE_TOKEN); } catch (e) { return null; }
}

async function fetchAutenticato(url, options = {}) {
  const token = tokenCorrente();
  const headers = { ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(url, { ...options, headers });
  if (res.status === 401) {
    onNonAutorizzato();
  }
  return res;
}

async function gestisciRisposta(res) {
  if (!res.ok) {
    let messaggio = `Errore ${res.status}`;
    try {
      const body = await res.json();
      if (body.errore || body.error) messaggio = body.errore || body.error;
    } catch (_) {}
    throw new Error(messaggio);
  }
  return res;
}

// --- Autenticazione ---
export async function login(azienda, username, password) {
  const res = await fetch("/api/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ azienda, username, password }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.ok) throw new Error(data.error || data.errore || "Utente o password non validi");
  return data; // { ok, token, username, puoMappare, azienda }
}

// Seconda password, distinta dal login: protegge il pannello Impostazioni.
export async function verificaPasswordAdmin(password) {
  const res = await fetchAutenticato("/api/verifica-password-admin", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ password }),
  });
  await gestisciRisposta(res);
  return res.json(); // { ok }
}

export async function getUtenti() {
  const res = await fetchAutenticato("/api/utenti");
  await gestisciRisposta(res);
  return res.json();
}

export async function salvaUtente({ username, password, puoMappare, azienda }) {
  const res = await fetchAutenticato("/api/utenti", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password, puoMappare, azienda }),
  });
  await gestisciRisposta(res);
  return res.json();
}

export async function eliminaUtente(username) {
  const res = await fetchAutenticato(`/api/utenti/${encodeURIComponent(username)}`, { method: "DELETE" });
  await gestisciRisposta(res);
  return res.json();
}

// Cambia solo il permesso "può mappare" di un utente esistente, senza
// toccarne la password — usato dallo switch nel pannello Impostazioni.
export async function impostaPermessoMappatura(username, puoMappare) {
  const res = await fetchAutenticato(`/api/utenti/${encodeURIComponent(username)}/permessi`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ puoMappare }),
  });
  await gestisciRisposta(res);
  return res.json();
}

// Cambia solo l'azienda (tenant) di un utente esistente, senza toccarne la
// password — stessa idea di impostaPermessoMappatura qui sopra.
export async function impostaAziendaUtente(username, azienda) {
  const res = await fetchAutenticato(`/api/utenti/${encodeURIComponent(username)}/azienda`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ azienda }),
  });
  await gestisciRisposta(res);
  return res.json();
}

// Elenco delle aziende note (per l'autocompletamento) — solo wtrendy.
export async function getAziende() {
  const res = await fetchAutenticato("/api/aziende");
  await gestisciRisposta(res);
  return res.json();
}

// --- Assegnazione template/modelli PDF alle aziende (pannello Impostazioni,
// solo wtrendy): elenco combinato di tutte le risorse salvate (di ogni
// modulo) con le aziende a cui sono assegnate, e l'endpoint per cambiarle. ---
export async function getRisorseAdmin() {
  const res = await fetchAutenticato("/api/admin/risorse");
  await gestisciRisposta(res);
  return res.json();
}

export async function assegnaAziendeRisorsa({ tipo, modulo, id, aziende }) {
  const res = await fetchAutenticato("/api/admin/risorse/assegna", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ tipo, modulo, id, aziende }),
  });
  await gestisciRisposta(res);
  return res.json();
}

// --- Moduli attivi: quali tab (diba/ddt/ordini) mostrare in "Cosa vuoi
// importare?" — scelta per azienda (vedi routes/auth.js moduliAttiviDiUtente):
// questa legge sempre e solo quelli dell'utente/azienda loggato in questo
// momento. Per cambiarli (solo wtrendy) vedi impostaModuliUtente più sotto.
export async function getModuliAttivi() {
  const res = await fetchAutenticato("/api/moduli-attivi");
  await gestisciRisposta(res);
  return res.json();
}

// Cambia solo i moduli attivi di un utente/azienda, senza toccare password/
// permesso/azienda — stessa idea di impostaPermessoMappatura/impostaAziendaUtente.
export async function impostaModuliUtente(username, moduliAttivi) {
  const res = await fetchAutenticato(`/api/utenti/${encodeURIComponent(username)}/moduli`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ moduliAttivi }),
  });
  await gestisciRisposta(res);
  return res.json();
}

// --- Tracciato / import (Distinta Base, DDT, ...): tutte le funzioni sotto
// prendono un "modulo" ("diba" di default) che seleziona quale tracciato e
// quale cartella di template usare sul backend — vedi routes/api.js e
// src/moduli.js. ---
export async function getTracciato(modulo = "diba") {
  const res = await fetchAutenticato(`/api/tracciato?modulo=${modulo}`);
  await gestisciRisposta(res);
  return res.json();
}

export async function caricaFile(file, { foglio, rigaIntestazioni, rigaIntestazioniSecondaria } = {}, modulo, forzaSceltaManuale = false) {
  const formData = new FormData();
  formData.append("file", file);
  if (foglio) formData.append("foglio", foglio);
  if (rigaIntestazioni !== undefined && rigaIntestazioni !== null) formData.append("rigaIntestazioni", String(rigaIntestazioni));
  // Intestazioni su due righe (es. "ITALY SIZE" unita sopra + nome taglia
  // vero sotto — vedi SceltaIntestazioneModal.jsx): il backend usa il
  // valore di questa riga per una colonna quando non è vuoto, altrimenti
  // quello di "rigaIntestazioni".
  if (rigaIntestazioniSecondaria !== undefined && rigaIntestazioniSecondaria !== null) {
    formData.append("rigaIntestazioniSecondaria", String(rigaIntestazioniSecondaria));
  }
  // Serve SOLO per il riconoscimento automatico "a freddo" (nessun template
  // ancora scelto: il backend prova a riconoscere da solo foglio/riga/
  // template salvati guardando i nomi di foglio salvati — vedi
  // provaRiconoscimentoAutomatico in routes/api.js): senza sapere il modulo
  // non saprebbe in quale cartella di template cercare.
  if (modulo) formData.append("modulo", modulo);
  // "Modifica" su un template già salvato: ricarica il file da capo ma
  // ignora sia il riconoscimento automatico che l'euristica sulla riga
  // intestazioni, chiedendo sempre foglio/riga a mano (vedi routes/api.js).
  if (forzaSceltaManuale) formData.append("forzaSceltaManuale", "true");
  const res = await fetchAutenticato("/api/upload", { method: "POST", body: formData });
  await gestisciRisposta(res);
  return res.json();
  // Possibili esiti "intermedi" (nessun fileId ancora, va richiamata di nuovo con la scelta):
  //  - { serveSceltaFoglio: true, fogli, nomeOriginale } — file con più fogli, nessuno scelto
  //  - { serveSceltaRigaIntestazioni: true, righeAnteprima, nomeOriginale, nomeFoglio } — prima riga
  //    non sembra l'intestazione (es. titolo/riga vuota sopra i nomi veri)
  // Sull'esito riuscito, "templateRiconosciuto: {id, cliente} | null" indica se
  // il riconoscimento automatico a freddo ha trovato un template da riapplicare.
}

export async function getTemplates(modulo = "diba") {
  const res = await fetchAutenticato(`/api/templates?modulo=${modulo}`);
  await gestisciRisposta(res);
  return res.json();
}

export async function getTemplate(id, modulo = "diba") {
  const res = await fetchAutenticato(`/api/templates/${id}?modulo=${modulo}`);
  await gestisciRisposta(res);
  return res.json();
}

// Tra i template salvati, quale corrisponde meglio alle colonne di questo
// file (per nomi di colonna sorgente) — usato per proporlo da solo invece
// di far scegliere sempre a mano, stessa idea del riconoscimento PDF.
export async function rilevaTemplate(headers, modulo = "diba") {
  const res = await fetchAutenticato("/api/rileva-template", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ headers, modulo }),
  });
  await gestisciRisposta(res);
  return res.json();
}

// Suggerimenti di mapping dallo storico dei template (vedi routes/api.js
// /suggerisci-mapping): per un file di layout nuovo, propone per ogni campo
// del tracciato la colonna sorgente / il valore fisso usati più spesso in
// passato. Da applicare solo ai campi ancora vuoti.
export async function suggerisciMapping(headers, modulo = "diba") {
  const res = await fetchAutenticato("/api/suggerisci-mapping", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ headers, modulo }),
  });
  await gestisciRisposta(res);
  return res.json(); // { suggerimenti: { <chiave>: {...} }, totaleTemplate }
}

// Apre il selettore di cartella nativo di Windows (vedi routes/api.js
// /scegli-cartella): la richiesta resta in sospeso finché l'utente non
// sceglie una cartella o annulla la finestra. percorso è "" se annullata.
export async function scegliCartella() {
  const res = await fetchAutenticato("/api/scegli-cartella", { method: "POST" });
  await gestisciRisposta(res);
  return res.json(); // { percorso }
}

export async function salvaTemplate({ id, cliente, mapping, colonneNascoste, cartellaDestinazione, modulo = "diba", impostazioniFile }) {
  const res = await fetchAutenticato("/api/templates", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id, cliente, mapping, colonneNascoste, cartellaDestinazione, modulo, impostazioniFile }),
  });
  await gestisciRisposta(res);
  return res.json();
}

export async function eliminaTemplate(id, modulo = "diba") {
  const res = await fetchAutenticato(`/api/templates/${id}?modulo=${modulo}`, { method: "DELETE" });
  await gestisciRisposta(res);
  return res.json();
}

// --- Import ordini: mappatura nativa ---
export async function getCampiOrdini() {
  const res = await fetchAutenticato("/api/ordini/campi");
  await gestisciRisposta(res);
  return res.json();
}

export async function getTemplatesOrdini() {
  const res = await fetchAutenticato("/api/ordini/templates");
  await gestisciRisposta(res);
  return res.json();
}

export async function getTemplateOrdini(id) {
  const res = await fetchAutenticato(`/api/ordini/templates/${id}`);
  await gestisciRisposta(res);
  return res.json();
}

export async function rilevaTemplateOrdini(headers) {
  const res = await fetchAutenticato("/api/ordini/rileva-template", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ headers }),
  });
  await gestisciRisposta(res);
  return res.json();
}

// Suggerimenti di mapping ordini dallo storico dei template (vedi
// routes/ordini.js /ordini/suggerisci-mapping) — gemello di suggerisciMapping
// per Distinta Base/DDT.
export async function suggerisciMappingOrdini(headers) {
  const res = await fetchAutenticato("/api/ordini/suggerisci-mapping", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ headers }),
  });
  await gestisciRisposta(res);
  return res.json(); // { suggerimenti: { <campo>: {...} }, totaleTemplate }
}

export async function salvaTemplateOrdini({ id, cliente, mapping, azienda, impostazioniFile }) {
  const res = await fetchAutenticato("/api/ordini/templates", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id, cliente, mapping, azienda, impostazioniFile }),
  });
  await gestisciRisposta(res);
  return res.json();
}

export async function eliminaTemplateOrdini(id) {
  const res = await fetchAutenticato(`/api/ordini/templates/${id}`, { method: "DELETE" });
  await gestisciRisposta(res);
  return res.json();
}

// --- Legenda sviluppi: elenco sviluppi validi + corrispondenze "nome
// calzata trovato -> sviluppo" per cliente — vedi routes/ordini.js e il
// bottone "🔤 Sviluppi" nell'anteprima di OrdiniMapping.jsx. ---
// Legenda sviluppi (per AZIENDA, non per cliente/brand — vedi routes/ordini.js):
// GET è per chiunque sia autenticato (l'operatore vede solo la propria
// azienda, wtrendy vede qualunque azienda gli si chieda).
export async function getLegendaSviluppi(azienda) {
  const res = await fetchAutenticato(`/api/ordini/legenda-sviluppi/${encodeURIComponent(azienda)}`);
  await gestisciRisposta(res);
  return res.json();
}

// Aggiunge sviluppi al vocabolario di un'azienda — SOLO wtrendy (pannello
// Impostazioni): l'endpoint stesso rifiuta la richiesta a chiunque altro.
export async function salvaSviluppiAzienda(azienda, sviluppi) {
  const res = await fetchAutenticato(`/api/ordini/legenda-sviluppi/${encodeURIComponent(azienda)}/sviluppi`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ sviluppi }),
  });
  await gestisciRisposta(res);
  return res.json();
}

export async function eliminaSviluppoAzienda(azienda, nome) {
  const res = await fetchAutenticato(
    `/api/ordini/legenda-sviluppi/${encodeURIComponent(azienda)}/sviluppo/${encodeURIComponent(nome)}`,
    { method: "DELETE" }
  );
  await gestisciRisposta(res);
  return res.json();
}

// Salva le corrispondenze "valore trovato nel file -> sviluppo già
// esistente" — questa la può fare qualunque operatore (niente permesso di
// mappatura richiesto), ma solo per la propria azienda, e solo verso
// sviluppi già presenti nel vocabolario (l'endpoint scarta il resto).
export async function salvaMappatureSviluppi(azienda, mappature) {
  const res = await fetchAutenticato(`/api/ordini/legenda-sviluppi/${encodeURIComponent(azienda)}/mappature`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ mappature }),
  });
  await gestisciRisposta(res);
  return res.json();
}

// Conversione codici (per TEMPLATE, non azienda — vedi routes/ordini.js):
// GET è per chiunque veda il template. Non c'è un vocabolario curato come
// per gli sviluppi: il codice giusto è testo libero scritto da chi risolve.
export async function getConversioneCodici(templateId) {
  const res = await fetchAutenticato(`/api/ordini/conversione-codici/${encodeURIComponent(templateId)}`);
  await gestisciRisposta(res);
  return res.json();
}

// Salva le corrispondenze "codice fornitore trovato nel file -> mio codice"
// per UN campo (es. Riga_Articolo) — qualunque operatore che vede il
// template, niente permesso di mappatura richiesto.
export async function salvaMappatureConversione(templateId, campo, mappature) {
  const res = await fetchAutenticato(`/api/ordini/conversione-codici/${encodeURIComponent(templateId)}/mappature`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ campo, mappature }),
  });
  await gestisciRisposta(res);
  return res.json();
}

// Elimina una o più corrispondenze in un colpo solo — voci: [{templateId, campo, codice}]
export async function eliminaMappatureConversioneMultiple(voci) {
  const res = await fetchAutenticato(`/api/ordini/conversione-codici/elimina-multiple`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ voci }),
  });
  await gestisciRisposta(res);
  return res.json();
}

// Elenco delle conversioni codici (di tutti i template visibili all'utente,
// o solo di UNA azienda se passata) che hanno almeno una corrispondenza
// salvata — per il pannello Impostazioni, tab azienda.
export async function getElencoConversioniCodici(azienda) {
  const query = azienda ? `?azienda=${encodeURIComponent(azienda)}` : "";
  const res = await fetchAutenticato(`/api/ordini/conversione-codici${query}`);
  await gestisciRisposta(res);
  return res.json();
}

// --- Ticket di assistenza (vedi routes/ticket.js e src/ApriTicketModal.jsx):
// apre un ticket nel sistema wTrendy quando un file/PDF non viene
// riconosciuto, con l'eventuale file allegato. ---
export async function apriTicket({ email_utente, oggetto, descrizione, dettagli_tecnici, allegato, pdfId }) {
  const formData = new FormData();
  formData.append("email_utente", email_utente);
  formData.append("oggetto", oggetto);
  formData.append("descrizione", descrizione);
  if (dettagli_tecnici) formData.append("dettagli_tecnici", dettagli_tecnici);
  if (allegato) formData.append("allegato", allegato);
  if (pdfId) formData.append("pdfId", pdfId);
  const res = await fetchAutenticato("/api/ticket/apri", { method: "POST", body: formData });
  await gestisciRisposta(res);
  return res.json();
}

export async function applicaMappingOrdini({ fileId, mapping }) {
  const res = await fetchAutenticato("/api/ordini/applica-mapping", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ fileId, mapping }),
  });
  await gestisciRisposta(res);
  return res.json();
}

// Ricevono le righe già calcolate (e possibilmente corrette a mano in
// pagina, vedi anteprimaRighe/modificaCellaAnteprima in OrdiniMapping.jsx)
// invece di fileId+mapping: così quello che si vede in anteprima è
// esattamente quello che finisce nel file/nell'importazione, comprese le
// modifiche manuali.
export async function esportaAnteprimaOrdini({ righe }) {
  const res = await fetchAutenticato("/api/ordini/esporta-anteprima", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ righe }),
  });
  await gestisciRisposta(res);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "anteprima-ordine.xlsx";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function avviaImportazioneOrdini({ righe }) {
  const res = await fetchAutenticato("/api/ordini/importa", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ righe }),
  });
  await gestisciRisposta(res);
  return res.json();
}

export async function applicaMapping({ fileId, fileIds, mapping, modulo = "diba" }) {
  const res = await fetchAutenticato("/api/applica-mapping", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ fileId, fileIds, mapping, modulo }),
  });
  await gestisciRisposta(res);
  return res.json();
}

// --- Calibrazione PDF: un PDF non ha colonne vere, quindi va calibrato a
// mano (una volta per layout/cliente) prima di poterlo importare — vedi
// src/pages/CalibrazionePdf.jsx e routes/pdfModelli.js. ---
export async function caricaPdfGrezzo(file) {
  const formData = new FormData();
  formData.append("file", file);
  const res = await fetchAutenticato("/api/pdf/grezzo", { method: "POST", body: formData });
  await gestisciRisposta(res);
  return res.json();
}

export async function getFrammentiPdf(pdfId, pagina) {
  const res = await fetchAutenticato(`/api/pdf/grezzo/${pdfId}/frammenti?pagina=${pagina}`);
  await gestisciRisposta(res);
  return res.json();
}

// Bytes del PDF grezzo per il rendering lato browser (pdfjs-dist): via
// fetchAutenticato perché serve comunque il token — un semplice <img src=...>
// non potrebbe includerlo.
export async function scaricaBytesPdfGrezzo(pdfId) {
  const res = await fetchAutenticato(`/api/pdf/grezzo/${pdfId}`);
  await gestisciRisposta(res);
  return res.arrayBuffer();
}

export async function anteprimaCalibrazionePdf({ pdfId, calibrazione }) {
  const res = await fetchAutenticato("/api/pdf/anteprima", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ pdfId, calibrazione }),
  });
  await gestisciRisposta(res);
  return res.json();
}

export async function applicaModelloPdf(pdfId, modelloPdf) {
  const res = await fetchAutenticato(`/api/pdf/grezzo/${pdfId}/applica`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ modelloPdf }),
  });
  await gestisciRisposta(res);
  return res.json();
}

// I modelli PDF sono legati al modulo in cui sono stati calibrati (diba/
// ddt/ordini): stesso PDF di un cliente, ma letto in modo diverso a seconda
// del tracciato di destinazione, quindi restano completamente separati tra
// le tab — mai condivisi.
export async function getModelliPdf(modulo) {
  const res = await fetchAutenticato(`/api/pdf/modelli?modulo=${modulo}`);
  await gestisciRisposta(res);
  return res.json();
}

export async function getModelloPdf(id) {
  const res = await fetchAutenticato(`/api/pdf/modelli/${id}`);
  await gestisciRisposta(res);
  return res.json();
}

// Nomi di colonna/campo già usati negli altri modelli PDF dello stesso
// modulo (vedi routes/pdfModelli.js /pdf/vocabolario-colonne): usato dallo
// strumento "Riconosci le colonne" e per l'autocomplete dei nomi.
export async function getVocabolarioColonnePdf(modulo = "diba") {
  const res = await fetchAutenticato(`/api/pdf/vocabolario-colonne?modulo=${modulo}`);
  await gestisciRisposta(res);
  return res.json(); // { nomi: [{ nome, usato }] }
}

export async function salvaModelloPdf({ id, cliente, calibrazione, modulo }) {
  const res = await fetchAutenticato("/api/pdf/modelli", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id, cliente, calibrazione, modulo }),
  });
  await gestisciRisposta(res);
  return res.json();
}

export async function eliminaModelloPdf(id) {
  const res = await fetchAutenticato(`/api/pdf/modelli/${id}`, { method: "DELETE" });
  await gestisciRisposta(res);
  return res.json();
}

// Tra i modelli salvati, quale combacia meglio con questo PDF (per
// posizione dei campi calibrati) — usato per proporlo da solo invece di far
// scegliere sempre a mano.
export async function rilevaModelloPdf(pdfId, modulo) {
  const res = await fetchAutenticato("/api/pdf/rileva-modello", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ pdfId, modulo }),
  });
  await gestisciRisposta(res);
  return res.json();
}

// ggmmaaaa (es. 06082026): aggiunto al nome di ogni file esportato, così si
// capisce anche solo dal nome quando è stato generato rispetto alla diba
// originale caricata.
function dataEsportazione() {
  const d = new Date();
  const gg = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const aaaa = d.getFullYear();
  return `${gg}${mm}${aaaa}`;
}

// Genera e scarica il file finale: un xlsx se gruppi ha un solo elemento
// (un'unica combinazione articolo/variante/colore), oppure uno zip con dentro
// tutti gli xlsx se ce ne sono di più — il motore di import dell'ERP carica
// un file alla volta con un solo articolo/variante/combinazione, quindi con
// più combinazioni servono più file, ma li si scarica tutti insieme invece
// che uno alla volta. gruppi: [{ suffisso, righe }, ...] — vedi
// raggruppaPerCombinazione in pages/Revisione.jsx.
export async function esportaFile({ nomeFile, gruppi, modulo = "diba", cartellaDestinazione }) {
  const res = await fetchAutenticato("/api/esporta", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ nomeFile, gruppi, modulo, cartellaDestinazione }),
  });
  await gestisciRisposta(res);
  const cartellaSalvataHeader = res.headers.get("X-Cartella-Salvata");
  const avvisoHeader = res.headers.get("X-Avviso-Cartella");
  const cartellaSalvata = cartellaSalvataHeader ? decodeURIComponent(cartellaSalvataHeader) : "";
  const avviso = avvisoHeader ? decodeURIComponent(avvisoHeader) : "";
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const nomeBase = (nomeFile || "export-tracciato").replace(/[^a-z0-9_-]+/gi, "_");
  const estensione = blob.type.includes("zip") ? "zip" : "xlsx";
  a.download = `${nomeBase}_${dataEsportazione()}.${estensione}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return { cartellaSalvata, avviso };
}
